import React, {useCallback, useEffect, useMemo, useState} from 'react';
import styled from 'styled-components';
import * as THREE from 'three';
import {useAppSelector} from 'store/customer';
import {
    getRoomDimensions,
    getRoomType,
    getScale,
} from 'components/customer/RoomPlanner/store/roomPlannerSlice';
import {
    WALL_FLUSH_TOLERANCE_MM,
    calculateCabinetWallDistances,
    getTrueWallGapForArrow,
} from 'components/customer/RoomPlanner/helpers/measurement/wallDistance';
import {getWallDistanceTargetPosition} from 'components/customer/RoomPlanner/helpers/measurement/wallDistanceEdit';
import type {RoomConfig} from 'components/customer/RoomPlanner/helpers/measurement/types';
import {computeWorldBoundsExcludingAnimatedDoorParts} from 'components/customer/RoomPlanner/helpers/calculations';
import {
    getFloorClearanceMm,
    getSitOnFloorY,
    getWallTopY,
} from 'components/customer/RoomPlanner/helpers/placeholder/floorClearance';
import {
    isScene,
    syncOverlaysOnCabinetMove,
} from 'components/customer/RoomPlanner/helpers/syncOverlayOnCabinetMove';
import {shallowEqual} from 'react-redux';
import useAutoSave from 'components/customer/RoomPlanner/lib/useAutoSave';
import {useRoomPlannerContext} from 'components/customer/RoomPlanner/components/RoomPlannerContext';
import {
    useIsMobileLandscape,
    useIsPortraitDevices,
} from 'shared/helpers/DeviceSize';
import {
    mobileLandscape,
    mobileLandscapeAndPortraitDevices,
} from 'shared/Breakpoints';
import {Col, Row} from 'react-bootstrap';
import {CancelButton} from 'shared/components/CancelButton';
import {CBCButton} from 'shared/helpers';

// Interface for THREE.Group custom userData
interface CustomUserData {
    move?: (position: THREE.Vector3) => void;
    resetArrowVisibility?: () => void;
    onArrowClick?: (params: {
        direction: 'right' | 'left' | 'back' | 'front' | 'up' | 'down';
        screenX: number;
        screenY: number;
        arrowMesh: THREE.Mesh;
    }) => void;
    currentDirection?: string;
    movementArrows?: THREE.Mesh[];
    [key: string]: unknown; // Allow for other properties
}

type ComputedDimension = {
    computedWidth: number;
    roomWidth: number;
    roomDepth: number;
    roomDepthComputed: number;
    wallHeight: number;
    scale: number;
    roomLeftDepth: number;
    roomRightDepth: number;
    roomLeftWidth: number;
    roomRightWidth: number;
    positionX?: number;
    defaultPositionZ?: number;
};

interface DistanceEditorProps {
    activeDirection: 'right' | 'left' | 'back' | 'front' | 'up' | 'down' | null;
    inputRefs: {
        right: React.RefObject<HTMLInputElement>;
        left: React.RefObject<HTMLInputElement>;
        back: React.RefObject<HTMLInputElement>;
        front: React.RefObject<HTMLInputElement>;
        up: React.RefObject<HTMLInputElement>;
        down: React.RefObject<HTMLInputElement>;
    };
    onClose?: () => boolean;
}

const DistanceEditor: React.FC<DistanceEditorProps> = ({
    activeDirection,
    inputRefs,
    onClose,
}) => {
    const isMobileLandscape = useIsMobileLandscape();
    const isPortraitDevices = useIsPortraitDevices();
    const isMobileDevice = isMobileLandscape || isPortraitDevices;

    const {selectedCabinetGroup} = useRoomPlannerContext();
    const scale = useAppSelector(getScale);
    const {debouncedSave} = useAutoSave();
    const {rectangularDimension, lShapedDimension} = useAppSelector(
        getRoomDimensions,
        shallowEqual
    );
    const roomType = useAppSelector(getRoomType);

    // State for wall distances
    const [wallDistanceInputs, setWallDistanceInputs] = useState({
        left: '0',
        right: '0',
        back: '0',
        front: '0',
        top: '0',
        bottom: '0',
    });

    // Get cabinet dimensions
    const getCabinetDimensions = useCallback(() => {
        if (!selectedCabinetGroup) return {width: 0, height: 0, depth: 0};

        let width = 0;
        let height = 0;
        let depth = 0;

        // If userData doesn't have dimensions, try to compute from bounding box
        if (width === 0 && height === 0 && depth === 0) {
            // Create a bounding box for the entire group
            const bbox = new THREE.Box3().setFromObject(selectedCabinetGroup);
            width = bbox.max.x - bbox.min.x;
            height = bbox.max.y - bbox.min.y;
            depth = bbox.max.z - bbox.min.z;
        }

        return {width, height, depth};
    }, [selectedCabinetGroup]);

    // Get room dimensions computed
    const getRoomDimensionsComputed = useCallback((): ComputedDimension => {
        return {
            computedWidth:
                ((roomType === 'RECTANGULAR'
                    ? rectangularDimension.width
                    : lShapedDimension.rightWidth) /
                    2) *
                    -scale +
                26.1,
            roomWidth:
                (roomType === 'RECTANGULAR'
                    ? rectangularDimension.width
                    : lShapedDimension.rightWidth) * scale,
            roomDepth:
                roomType === 'RECTANGULAR'
                    ? rectangularDimension.length
                    : lShapedDimension.leftWidth,
            roomDepthComputed:
                ((roomType === 'RECTANGULAR'
                    ? rectangularDimension.length
                    : lShapedDimension.leftWidth) /
                    2) *
                    -scale -
                25,
            wallHeight:
                (roomType === 'RECTANGULAR'
                    ? rectangularDimension.height
                    : lShapedDimension.height) * scale,
            scale,
            roomLeftDepth: lShapedDimension.leftDepth || 0,
            roomRightDepth: lShapedDimension.rightDepth || 0,
            roomLeftWidth: lShapedDimension.leftWidth || 0,
            roomRightWidth: lShapedDimension.rightWidth || 0,
        };
    }, [roomType, rectangularDimension, lShapedDimension]);

    // Update arrow positions
    const updateArrowPositions = useCallback(() => {
        if (!selectedCabinetGroup) return;

        const scene = selectedCabinetGroup.parent;
        if (!isScene(scene)) return;

        syncOverlaysOnCabinetMove(selectedCabinetGroup, scene);

        const selectedId = selectedCabinetGroup.userData.id as string;
        scene.children.forEach((child: THREE.Object3D) => {
            if (
                child.name !== 'cabinetMovementArrows' ||
                child.userData?.id !== selectedId
            ) {
                return;
            }

            child.children.forEach((arrow) => {
                if (arrow.userData?.isArrow && arrow.userData.isHovered) {
                    if (typeof arrow.userData.onHoverEnd === 'function') {
                        (arrow.userData.onHoverEnd as () => void)();
                    }
                    arrow.userData.isHovered = false;
                }
            });
        });
    }, [selectedCabinetGroup]);

    // Prefill wall distances from the same helper used by blue measurement arrows
    const calculateWallDistances = useCallback(() => {
        if (!selectedCabinetGroup || !rectangularDimension) return;

        const dimensions = getRoomDimensionsComputed();
        const {height} = getCabinetDimensions();
        const halfHeight = height / 2;
        const position = selectedCabinetGroup.position;
        const currentDirection =
            (selectedCabinetGroup.userData as CustomUserData)
                .currentDirection || 'front';

        const roomConfig: RoomConfig = {
            roomType: roomType ?? 'RECTANGULAR',
            rectangularDimension: {
                width: rectangularDimension.width,
                length: rectangularDimension.length,
                height: rectangularDimension.height,
            },
            lShapedDimension: {
                rightWidth: lShapedDimension.rightWidth,
                leftWidth: lShapedDimension.leftWidth,
                rightDepth: lShapedDimension.rightDepth,
                leftDepth: lShapedDimension.leftDepth,
                height: lShapedDimension.height,
            },
            scale,
        };

        const distances = calculateCabinetWallDistances(
            selectedCabinetGroup,
            roomConfig
        );

        // Bottom (and placeholder top) use mesh AABB vs room planes so GLB
        // pivots that are not at the geometric centre still read correctly.
        const bottomClearanceMm = getFloorClearanceMm(
            selectedCabinetGroup,
            dimensions.wallHeight,
            scale
        );
        let topDistance =
            (dimensions.wallHeight / 2 - (position.y + halfHeight)) * 5;
        if (selectedCabinetGroup.userData?.isPlaceholder) {
            const box =
                computeWorldBoundsExcludingAnimatedDoorParts(
                    selectedCabinetGroup
                );
            const ceilingY = getWallTopY(dimensions.wallHeight);
            topDistance = Number.isFinite(box.max.y)
                ? (ceilingY - box.max.y) * 5
                : topDistance;
        }

        const snapFlush = (mmStr: string, dir: string): string => {
            const gap = getTrueWallGapForArrow(
                selectedCabinetGroup,
                currentDirection,
                dir
            );
            if (gap !== null && gap / scale <= WALL_FLUSH_TOLERANCE_MM) {
                return '0';
            }
            return mmStr;
        };

        const toMm = (scaled: number) =>
            Math.max(0, Math.round(Math.abs(scaled) / scale)).toString();

        setWallDistanceInputs((prevInputs) => {
            const newLeft = snapFlush(toMm(distances.left), 'left');
            const newRight = snapFlush(toMm(distances.right), 'right');
            const newBack = snapFlush(toMm(distances.back), 'back');
            const newFront = snapFlush(toMm(distances.front), 'front');
            const newTop = Math.round(topDistance + 50).toString();
            const newBottom = bottomClearanceMm.toString();

            if (
                prevInputs.left !== newLeft ||
                prevInputs.right !== newRight ||
                prevInputs.back !== newBack ||
                prevInputs.front !== newFront ||
                prevInputs.top !== newTop ||
                prevInputs.bottom !== newBottom
            ) {
                return {
                    left: newLeft,
                    right: newRight,
                    back: newBack,
                    front: newFront,
                    top: newTop,
                    bottom: newBottom,
                };
            }
            return prevInputs;
        });
    }, [
        selectedCabinetGroup,
        rectangularDimension,
        lShapedDimension,
        roomType,
        scale,
        getRoomDimensionsComputed,
        getCabinetDimensions,
    ]);

    // Apply wall distance by moving the cabinet by (target − current) on the
    // correct axis — same measurement basis as the blue arrows / prefill.
    const applyWallDistanceChange = useCallback(
        (
            wall: 'left' | 'right' | 'back' | 'front' | 'top' | 'bottom',
            value: string
        ) => {
            if (!selectedCabinetGroup) return;

            const numValue = value === '' ? 0 : parseFloat(value);
            if (isNaN(numValue)) return;

            const {height} = getCabinetDimensions();
            const halfHeight = height / 2;
            const dimensions = getRoomDimensionsComputed();

            let newX = selectedCabinetGroup.position.x;
            let newY = selectedCabinetGroup.position.y;
            let newZ = selectedCabinetGroup.position.z;

            const targetDistance = numValue * scale;

            if (
                wall === 'left' ||
                wall === 'right' ||
                wall === 'front' ||
                wall === 'back'
            ) {
                const roomConfig: RoomConfig = {
                    roomType: roomType ?? 'RECTANGULAR',
                    rectangularDimension: {
                        width: rectangularDimension.width,
                        length: rectangularDimension.length,
                        height: rectangularDimension.height,
                    },
                    lShapedDimension: {
                        rightWidth: lShapedDimension.rightWidth,
                        leftWidth: lShapedDimension.leftWidth,
                        rightDepth: lShapedDimension.rightDepth,
                        leftDepth: lShapedDimension.leftDepth,
                        height: lShapedDimension.height,
                    },
                    scale,
                };

                const target = getWallDistanceTargetPosition(
                    selectedCabinetGroup,
                    wall,
                    numValue,
                    roomConfig
                );
                newX = target.x;
                newZ = target.z;
            } else if (wall === 'top') {
                if (selectedCabinetGroup.userData?.isPlaceholder) {
                    // Keep mesh top at ceilingY − clearance (GLB pivot ≠ centre)
                    const box =
                        computeWorldBoundsExcludingAnimatedDoorParts(
                            selectedCabinetGroup
                        );
                    const ceilingY = getWallTopY(dimensions.wallHeight);
                    if (Number.isFinite(box.max.y)) {
                        const topOffset =
                            box.max.y - selectedCabinetGroup.position.y;
                        // Invert displayed (ceilingY − meshTop) * 5 + 50
                        newY = ceilingY - (numValue - 50) / 5 - topOffset;
                    } else {
                        newY =
                            dimensions.wallHeight / 2 -
                            numValue / 5 -
                            halfHeight +
                            10;
                    }
                } else {
                    newY =
                        dimensions.wallHeight / 2 -
                        numValue / 5 -
                        halfHeight +
                        10;
                }
            } else if (wall === 'bottom') {
                if (selectedCabinetGroup.userData?.isPlaceholder) {
                    // Keep mesh bottom at floorY + clearance (GLB pivot ≠ bottom)
                    const floorY = getSitOnFloorY(dimensions.wallHeight);
                    const box =
                        computeWorldBoundsExcludingAnimatedDoorParts(
                            selectedCabinetGroup
                        );
                    const bottomOffset =
                        box.min.y - selectedCabinetGroup.position.y;
                    newY = floorY + targetDistance - bottomOffset;
                } else {
                    newY = targetDistance - dimensions.wallHeight / 2 + 51;
                }
            }

            const newPosition = new THREE.Vector3(newX, newY, newZ);

            const userData = selectedCabinetGroup.userData as CustomUserData;
            if (userData.move) {
                userData.move(newPosition);
            } else {
                selectedCabinetGroup.position.copy(newPosition);
            }

            updateArrowPositions();
            calculateWallDistances();
            debouncedSave(selectedCabinetGroup);
        },
        [
            selectedCabinetGroup,
            getCabinetDimensions,
            rectangularDimension,
            lShapedDimension,
            roomType,
            getRoomDimensionsComputed,
            updateArrowPositions,
            calculateWallDistances,
            debouncedSave,
            scale,
        ]
    );

    // Function to handle changes to wall distance inputs with immediate application
    const handleWallDistanceChange = useCallback(
        (
            wall: 'left' | 'right' | 'back' | 'front' | 'top' | 'bottom',
            value: string
        ) => {
            // Only allow numeric input (integers and decimals)
            // Allow empty string, numbers, and decimal point
            if (value === '' || /^\d*\.?\d*$/.test(value)) {
                setWallDistanceInputs((prev) => ({
                    ...prev,
                    [wall]: value,
                }));
            }
        },
        []
    );

    // Add confirm handler
    const handleConfirm = useCallback(() => {
        if (!activeDirection) return;

        // Map up/down to top/bottom
        const mappedWall = (() => {
            switch (activeDirection.toLowerCase()) {
                case 'up':
                    return 'top' as const;
                case 'down':
                    return 'bottom' as const;
                case 'left':
                    return 'left' as const;
                case 'right':
                    return 'right' as const;
                case 'back':
                    return 'back' as const;
                case 'front':
                    return 'front' as const;
                default:
                    return null;
            }
        })();

        if (!mappedWall) return;

        // Use switch statement for type-safe access
        switch (mappedWall) {
            case 'left':
                applyWallDistanceChange('left', wallDistanceInputs.left);
                break;
            case 'right':
                applyWallDistanceChange('right', wallDistanceInputs.right);
                break;
            case 'back':
                applyWallDistanceChange('back', wallDistanceInputs.back);
                break;
            case 'front':
                applyWallDistanceChange('front', wallDistanceInputs.front);
                break;
            case 'top':
                applyWallDistanceChange('top', wallDistanceInputs.top);
                break;
            case 'bottom':
                applyWallDistanceChange('bottom', wallDistanceInputs.bottom);
                break;
        }

        // Close the editor after confirming
        if (onClose) {
            onClose();
        }
    }, [activeDirection, wallDistanceInputs, applyWallDistanceChange, onClose]);

    // Create handlers for each wall direction
    const handleRightWallChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            handleWallDistanceChange('right', e.target.value);
        },
        [handleWallDistanceChange]
    );

    const handleLeftWallChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            handleWallDistanceChange('left', e.target.value);
        },
        [handleWallDistanceChange]
    );

    const handleBackWallChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            handleWallDistanceChange('back', e.target.value);
        },
        [handleWallDistanceChange]
    );

    const handleFrontWallChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            handleWallDistanceChange('front', e.target.value);
        },
        [handleWallDistanceChange]
    );

    const handleTopWallChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            handleWallDistanceChange('top', e.target.value);
        },
        [handleWallDistanceChange]
    );

    const handleBottomWallChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            handleWallDistanceChange('bottom', e.target.value);
        },
        [handleWallDistanceChange]
    );

    // Add keyboard event handlers for Enter key
    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
                handleConfirm();
            }
        },
        [handleConfirm]
    );

    // Calculate wall distances when selectedCabinetGroup changes
    useEffect(() => {
        if (selectedCabinetGroup && selectedCabinetGroup.position) {
            // Use a timeout to prevent too frequent updates
            const timeoutId = setTimeout(() => {
                calculateWallDistances();
            }, 50);

            return () => clearTimeout(timeoutId);
        }
    }, [selectedCabinetGroup, calculateWallDistances]);

    // Auto-focus the input when the editor opens
    useEffect(() => {
        if (activeDirection) {
            let currentRef: React.RefObject<HTMLInputElement> | null = null;

            switch (activeDirection) {
                case 'right':
                    currentRef = inputRefs.right;
                    break;
                case 'left':
                    currentRef = inputRefs.left;
                    break;
                case 'back':
                    currentRef = inputRefs.back;
                    break;
                case 'front':
                    currentRef = inputRefs.front;
                    break;
                case 'up':
                    currentRef = inputRefs.up;
                    break;
                case 'down':
                    currentRef = inputRefs.down;
                    break;
            }

            if (currentRef?.current) {
                // Small timeout to ensure the input is rendered
                setTimeout(() => {
                    currentRef.current?.focus();
                    currentRef.current?.select();
                }, 100);
            }
        }
    }, [activeDirection, inputRefs]);

    const properties = useMemo(() => {
        if (activeDirection === 'right') {
            return {
                ref: inputRefs.right,
                value: wallDistanceInputs.right,
                changeHandler: handleRightWallChange,
            };
        } else if (activeDirection === 'left') {
            return {
                ref: inputRefs.left,
                value: wallDistanceInputs.left,
                changeHandler: handleLeftWallChange,
            };
        } else if (activeDirection === 'back') {
            return {
                ref: inputRefs.back,
                value: wallDistanceInputs.back,
                changeHandler: handleBackWallChange,
            };
        } else if (activeDirection === 'front') {
            return {
                ref: inputRefs.front,
                value: wallDistanceInputs.front,
                changeHandler: handleFrontWallChange,
            };
        } else if (activeDirection === 'up') {
            return {
                ref: inputRefs.up,
                value: wallDistanceInputs.top,
                changeHandler: handleTopWallChange,
            };
        } else if (activeDirection === 'down') {
            return {
                ref: inputRefs.down,
                value: wallDistanceInputs.bottom,
                changeHandler: handleBottomWallChange,
            };
        }
    }, [
        activeDirection,
        inputRefs,
        wallDistanceInputs,
        handleRightWallChange,
        handleLeftWallChange,
        handleBackWallChange,
        handleFrontWallChange,
        handleTopWallChange,
        handleBottomWallChange,
    ]);

    if (!activeDirection) return null;
    if (properties == undefined) return null;

    return (
        <>
            <EditorContent>
                <WallDistanceLabel>Distance from wall:</WallDistanceLabel>
                <PositionRow>
                    <InputWithDragHandle>
                        <Input
                            ref={properties.ref}
                            type="text"
                            inputMode="decimal"
                            value={properties.value}
                            onChange={properties.changeHandler}
                            onKeyDown={handleKeyDown}
                        />
                    </InputWithDragHandle>
                    <MeasurementDisplay>mm</MeasurementDisplay>
                    {isMobileDevice ? null : (
                        <StepButton onClick={handleConfirm}>Confirm</StepButton>
                    )}
                </PositionRow>
            </EditorContent>

            {isMobileDevice ? (
                <MobileControls className="control-buttons">
                    <Col xs={6}>
                        <CancelButton
                            noDialog={true}
                            noRedirect={true}
                            beforeRedirect={onClose}
                        />
                    </Col>
                    <Col xs={6}>
                        <CBCButton
                            onClick={handleConfirm}
                            type="submit"
                            iconName="Button-Tick.svg"
                            className="job-button button-blue">
                            Move
                        </CBCButton>
                    </Col>
                </MobileControls>
            ) : null}
        </>
    );
};

export default DistanceEditor;

export const MobileControls = styled(Row)`
    width: calc(100% + 20px);
    padding-bottom: 15px;
    margin-right: 0;
    margin-left: -10px;
    padding-top: 15px;
    box-shadow: 0 0 7px 0px #bbbbbb;

    > div:last-child {
        padding-right: 10px;
    }

    > div > button {
        width: 100%;
        padding: 5px;
        font-size: 0.85em;

        > img {
            width: 20px !important;
        }
    }

    ${mobileLandscape`
        margin-top: auto;    
    `}
`;

// Styled components
const EditorContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    position: relative;
    padding-right: 8px;

    ${mobileLandscapeAndPortraitDevices`
        flex-direction: row;   
        width: 100%;
        align-items: center; 
        gap: 15%;
    `}
`;

const InputWithDragHandle = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;

    ${mobileLandscapeAndPortraitDevices`
        flex: 1;
    `}
`;

const PositionRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
    width: 100%;

    ${mobileLandscapeAndPortraitDevices`
        width: initial;
        margin-bottom: 0;
        flex: 1;
    `}
`;

const Input = styled.input`
    background: #333;
    border: 1px solid #444;
    border-radius: 3px;
    color: #f1f1f1;
    padding: 4px 8px;
    width: 80px;
    font-size: 13px;
    &:focus {
        outline: none;
        border-color: #666;
    }

    ${mobileLandscapeAndPortraitDevices`
        width: 100%;
        background: white;
        color: black;
        border: 2px solid rgb(var(--primary_colour));
        border-radius: 8px;
        flex: 1;
        padding: 6px 10px;
        text-align: left;
        font-size: 1em;
        color: #4b4f52;

        &:focus, &:active {
            border-color: rgb(var(--primary_colour));
        }
    `}
`;

const MeasurementDisplay = styled.div`
    font-size: 11px;
    color: #888;
    margin-left: 1px;
    margin-right: 5px;

    ${mobileLandscapeAndPortraitDevices`
        font-size: 0.9em;
        color: #585b5e;
        font-weight: 500;
    `}
`;

const WallDistanceLabel = styled.div`
    font-size: 12px;
    color: #aaa;
    margin-bottom: 4px;

    ${mobileLandscapeAndPortraitDevices`
        font-weight: 500;
        color: rgb(var(--primary_colour));
        margin-bottom: 0;
        font-size: 0.95em;
    `}
`;

const StepButton = styled.button`
    background: #444;
    border: none;
    border-radius: 3px;
    color: #f1f1f1;
    width: 55px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-weight: bold;
    font-size: 12px;
    &:hover {
        background: #555;
    }
`;
